'use client';

import { useEffect } from 'react';
import { generateUuid } from '@/app_lib/utils/utils';

const BYOID_VISITOR_ID_COOKIE_NAME = 'BYOID_VisitorID';

interface UseByoidVisitorIdOptions {
  cookieName?: string;
  maxAge?: number;
}

/**
 * Sets a cookie with the given name, value, and options.
 */
function setCookie(
  name: string,
  value: string,
  options: { maxAge?: number; path?: string; secure?: boolean; sameSite?: 'lax' | 'strict' | 'none' } = {},
) {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  if (options.maxAge) {
    cookieString += `; max-age=${options.maxAge}`;
  }
  if (options.path) {
    cookieString += `; path=${options.path}`;
  }
  if (options.secure) {
    cookieString += '; secure';
  }
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }
  // Assign cookie using the standard API; this is the recommended way in browsers.
  window.document.cookie = cookieString;
}

/**
 * Custom hook to ensure BYOID_VisitorID cookie is set on client-side
 * Provides backup when server-side middleware doesn't set it properly
 */
function useByoidVisitorId({
  cookieName = BYOID_VISITOR_ID_COOKIE_NAME,
  maxAge = 365 * 2 * 24 * 60 * 60, // 2 years in seconds
}: UseByoidVisitorIdOptions = {}) {
  useEffect(() => {
    // Check if cookie already exists
    const existingCookie = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`));

    if (!existingCookie) {
      const id = generateUuid();
      setCookie(cookieName, id, {
        maxAge,
        path: '/',
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
      });
    }
  }, [cookieName, maxAge]);
}

export { useByoidVisitorId };
